export const defaultTheme = {
  primaryColor: '#4b830a',
  sideTheme: 'theme-dark'
}

const THEME_STORAGE_KEY = 'layout-setting'
const HEX_COLOR = /^#([0-9a-fA-F]{6})$/

export function normalizeTheme(theme = {}) {
  const primaryColor = HEX_COLOR.test(theme.primaryColor || '')
    ? theme.primaryColor.toLowerCase()
    : defaultTheme.primaryColor
  const sideTheme = theme.sideTheme === 'theme-light' ? 'theme-light' : defaultTheme.sideTheme
  return { primaryColor, sideTheme }
}

export function getCachedTheme() {
  try {
    const setting = JSON.parse(localStorage.getItem(THEME_STORAGE_KEY)) || {}
    return normalizeTheme({
      primaryColor: setting.primaryColor || setting.theme,
      sideTheme: setting.sideTheme
    })
  } catch (e) {
    return { ...defaultTheme }
  }
}

export function cacheTheme(theme) {
  const normalizedTheme = normalizeTheme(theme)
  const setting = JSON.parse(localStorage.getItem(THEME_STORAGE_KEY)) || {}
  localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({
    ...setting,
    theme: normalizedTheme.primaryColor,
    primaryColor: normalizedTheme.primaryColor,
    sideTheme: normalizedTheme.sideTheme
  }))
  return normalizedTheme
}

export function applyTheme(theme) {
  const normalizedTheme = normalizeTheme(theme)
  const palette = buildPalette(normalizedTheme.primaryColor)
  const root = document.documentElement
  root.style.setProperty('--theme-primary', normalizedTheme.primaryColor)
  root.style.setProperty('--current-color', normalizedTheme.primaryColor)
  root.style.setProperty('--theme-primary-hover', palette.hover)
  root.style.setProperty('--theme-primary-active', palette.active)
  root.style.setProperty('--theme-primary-light', palette.light)
  root.style.setProperty('--theme-primary-lighter', palette.lighter)
  root.style.setProperty('--theme-menu-active-bg', hexToRgba(normalizedTheme.primaryColor, 0.53))
  root.style.setProperty('--theme-menu-hover-bg', hexToRgba(normalizedTheme.primaryColor, 0.53))
  root.style.setProperty('--theme-sidebar-bg', normalizedTheme.sideTheme === 'theme-light' ? '#ffffff' : '#304156')
  root.style.setProperty('--theme-sidebar-text', normalizedTheme.sideTheme === 'theme-light' ? '#303133' : '#bfcbd9')
  root.style.setProperty('--theme-sidebar-active-text', normalizedTheme.primaryColor)
  return normalizedTheme
}

function buildPalette(hex) {
  return {
    hover: mix(hex, '#ffffff', 0.16),
    active: mix(hex, '#000000', 0.12),
    light: mix(hex, '#ffffff', 0.82),
    lighter: mix(hex, '#ffffff', 0.92)
  }
}

function mix(color, mixColor, weight) {
  const rgb = hexToRgb(color)
  const mixRgb = hexToRgb(mixColor)
  return rgbToHex({
    r: Math.round(rgb.r * (1 - weight) + mixRgb.r * weight),
    g: Math.round(rgb.g * (1 - weight) + mixRgb.g * weight),
    b: Math.round(rgb.b * (1 - weight) + mixRgb.b * weight)
  })
}

function hexToRgba(hex, alpha) {
  const rgb = hexToRgb(hex)
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function hexToRgb(hex) {
  const value = hex.replace('#', '')
  return {
    r: parseInt(value.substring(0, 2), 16),
    g: parseInt(value.substring(2, 4), 16),
    b: parseInt(value.substring(4, 6), 16)
  }
}

function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map(value => value.toString(16).padStart(2, '0')).join('')
}
